/**
 * A copy of something being written, kept on this device until the server
 * has it.
 *
 * For the reps this module is built for, "no signal" is a normal state -- a
 * car park, a basement plant room, a train. The server is still where notes
 * and drafts live, so they follow somebody from phone to laptop; this is the
 * safety net underneath it. Every edit is copied here as it happens, the copy
 * is cleared once the server confirms it has the same text, and anything
 * still here when the page next opens was never saved and is put back.
 *
 * Browser storage can be missing or refuse writes (private mode, a full
 * disk), so every call fails quietly: losing the safety net must never stop
 * somebody typing.
 */

interface Copy<T> {
  value: T;
  /** When it was written here, as epoch milliseconds. */
  at: number;
}

export function readCopy<T>(key: string): Copy<T> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Copy<T>;
    return typeof parsed?.at === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export function writeCopy<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({ value, at: Date.now() }));
  } catch {
    /* Storage refused. The server copy is still the real one. */
  }
}

export function clearCopy(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* Nothing to do. */
  }
}

/**
 * The newer of what the server has and what this device kept.
 *
 * The device's copy wins only when it is newer than the server's last save
 * and actually differs -- an old copy left behind by a tab that closed
 * mid-save must not undo an edit made since on another device.
 */
export function newerOf<T>(
  server: { value: T; at: string | null } | null,
  device: Copy<T> | null,
  same: (a: T, b: T) => boolean,
): { value: T; fromDevice: boolean } | null {
  if (device && (!server || !server.at || device.at > Date.parse(server.at))) {
    if (!server || !same(server.value, device.value)) {
      return { value: device.value, fromDevice: true };
    }
  }
  return server ? { value: server.value, fromDevice: false } : null;
}
